import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { editPatientDetails, editPatientVitals } from "./../../Services/appointmentService";
import { useMasterData } from "../Common/commonEvents";
import { toInputDate } from './../../Utils/dateUtils';

export default function EditAppointmentModal({ show, appointment, onClose, onSuccess }) {
    const sessionUserData = JSON.parse(sessionStorage.getItem("userData") || "{}");

    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        appointmentId: "",
        patientId: "",

        name: "",
        mobile: "",
        email: "",
        age: "",
        gender: "1",
        address: "",
        dob: "",

        allergy: "",
        bp: "",
        diabetesDetails: "",
        epilepsy: false,
        asthma: false,
        otherConditions: "",
        medications: "",
        motes: "",

        weight: "",
        temperature: "",
        heartRate: "",
    });

    const Toast = Swal.mixin({
        toast: true,
        position: "top",
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
    });

    const {
        fetchAppointmentDetailsById,
        masterLoading,
      } = useMasterData();

      const bindAppointmentToForm = (data) => {
        const details = data?.appointment || data || {};
      
        setFormData({
          appointmentId: details.AppointmentId || details.Id || appointment?.AppointmentId || "",
          patientId: details.PatientId || appointment?.PatientId || "",
      
          name: details.PatientName || details.Name || "",
          mobile: details.Mobile || "",
          email: details.Email || "",
          age: details.Age || "",
          gender: String(details.Gender || "1"),
          address: details.Address || "",
          notes: details.Notes || "",
          dob: toInputDate(details.DOB),
      
          allergy: details.Allergy || "",
          bp: details.BP || "",
          diabetesDetails: Boolean(details.DiabetesDetails),
          epilepsy: Boolean(details.Epilepsy),
          asthma: Boolean(details.Asthma),
          otherConditions: details.OtherConditions || "",
          medications: details.Medications || "",
      
          weight: details.Weight || "",
          temperature: details.Temperature || "",
          heartRate: details.HeartRate || "",
        });
      };

      useEffect(() => {
        if (!show || !appointment) return;
      
        const loadAppointmentDetails = async () => {
          const appointmentId = appointment.AppointmentId || appointment.Id;
      
          if (!sessionUserData?.OrgId || !appointmentId) return;
      
          const data = await fetchAppointmentDetailsById(
            sessionUserData.OrgId,
            appointmentId
          );
      
          bindAppointmentToForm(data);
        };
      
        loadAppointmentDetails();
      }, [show, appointment?.AppointmentId, appointment?.Id, sessionUserData?.OrgId]);

    useEffect(() => {
        if (!show || !appointment) return;

        setFormData({
            appointmentId: appointment.AppointmentId || appointment.Id || "",
            patientId: appointment.PatientId || "",

            name: appointment.PatientName || appointment.Name || appointment.patient || "",
            mobile: appointment.Mobile || appointment.mobile || "",
            email: appointment.Email || appointment.email || "",
            age: appointment.Age || appointment.age || "",
            gender: String(appointment.Gender || appointment.gender || "1"),
            address: appointment.Address || appointment.address || "",
            dob: appointment.DOB ? appointment.DOB.split("T")[0] : "",

            allergy: appointment.Allergy || "",
            bp: appointment.BP || "",
            diabetesDetails: Boolean(appointment.DiabetesDetails),
            epilepsy: Boolean(appointment.Epilepsy),
            asthma: Boolean(appointment.Asthma),
            otherConditions: appointment.OtherConditions || "",
            medications: appointment.Medications || "",

            weight: appointment.Weight || "",
            temperature: appointment.Temperature || "",
            heartRate: appointment.HeartRate || "",
        });
    }, [show, appointment]);

    useEffect(() => {
        if (!show) return;

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [show]);

    if (!show) return null;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleSubmit = async (e) => {

        e.preventDefault();
    
        try {
    
            setLoading(true);
    
            // =========================
            // EDIT PATIENT PAYLOAD
            // =========================
    
            const patientPayload = {
    
                OrgId: sessionUserData?.OrgId || 1,
    
                UserId: sessionUserData?.Id || 1,
    
                Type: "EDITPATIENT",
    
                JsonData: {
    
                    PatientId:
                        Number(formData.patientId),
    
                    Name: formData.name,
    
                    Mobile: formData.mobile,
    
                    Email: formData.email,
    
                    Age:
                        Number(formData.age) || 0,
    
                    Gender:
                        Number(formData.gender) || 1,
    
                    Address: formData.address,
    
                    DOB: formData.dob || null,
                },
            };
    
            // =========================
            // EDIT VITALS PAYLOAD
            // =========================
    
            const vitalsPayload = {
    
                OrgId: sessionUserData?.OrgId || 1,
    
                UserId: sessionUserData?.Id || 1,
    
                Type: "EDITVITALS",
    
                JsonData: {
    
                    AppointmentId:
                        Number(formData.appointmentId),
    
                    Allergy:
                        formData.allergy || "None",
    
                    BP: formData.bp,
    
                    DiabetesDetails:
                        formData.diabetesDetails,
    
                    Epilepsy:
                        formData.epilepsy ? 1 : 0,
    
                    Asthma:
                        formData.asthma ? 1 : 0,
    
                    OtherConditions:
                        formData.otherConditions,
    
                    Medications:
                        formData.medications,
    
                    Weight:
                        formData.weight,
    
                    Temperature:
                        formData.temperature,
    
                    HeartRate:
                        formData.heartRate,
                },
            };
    
            // =========================
            // API CALLS
            // =========================
    
            const patientResponse =
                await editPatientDetails(
                    patientPayload
                );
    
            const vitalsResponse =
                await editPatientVitals(
                    vitalsPayload
                );
    
            // =========================
            // SUCCESS CHECK
            // =========================
    
            if (
                patientResponse?.success &&
                vitalsResponse?.success
            ) {
    
                Toast.fire({
                    icon: "success",
                    title:
                        "Patient details updated successfully",
                });
    
                onSuccess?.();
    
                onClose();
    
            } else {
    
                Toast.fire({
                    icon: "error",
                    title:
                        patientResponse?.message ||
                        vitalsResponse?.message ||
                        "Failed to update details",
                });
            }
    
        } catch (error) {
    
            console.error(
                error?.response?.data ||
                error?.message
            );
    
            Toast.fire({
                icon: "error",
                title:
                    error?.response?.data?.message ||
                    "Failed to update details",
            });
    
        } finally {
    
            setLoading(false);
        }
    };

    return (
        <div
            className="modal show d-block"
            style={{
                background: "rgba(15, 23, 42, 0.62)",
                backdropFilter: "blur(6px)",
                overflowY: "auto",
                padding: "24px 12px",
            }}
        >
            <div
                className="modal-dialog modal-lg modal-dialog-centered"
                style={{
                    maxHeight: "calc(100vh - 48px)",
                    margin: "0 auto",
                }}
            >
                <div
                    className="modal-content border-0 shadow-lg overflow-hidden"
                    style={{
                        borderRadius: "22px",
                        maxHeight: "calc(100vh - 48px)",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <div
                        className="modal-header border-0 px-4 py-4"
                        style={{
                            background: "linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)",
                        }}
                    >
                        <div className="d-flex align-items-center gap-3">
                            <div
                                className="rounded-circle bg-white bg-opacity-25 d-flex align-items-center justify-content-center"
                                style={{ width: "52px", height: "52px" }}
                            >
                                <i className="bi bi-pencil-square text-white fs-3"></i>
                            </div>

                            <div>
                                <h5 className="modal-title fw-bold text-white mb-1">
                                    Edit Appointment
                                </h5>
                                <div className="text-white text-opacity-75 fs-7">
                                    Update appointment notes and medical details
                                </div>
                            </div>
                        </div>

                        <button
                            type="button"
                            className="btn btn-sm rounded-circle bg-white bg-opacity-25 border-0 text-white"
                            onClick={onClose}
                            style={{ width: "38px", height: "38px" }}
                        >
                            <i className="bi bi-x-lg text-white"></i>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
                        <div
                            className="modal-body p-4 bg-light"
                            style={{
                                overflowY: "auto",
                                flex: "1 1 auto",
                            }}
                        >
                            <div className="bg-white rounded-4 p-4 shadow-sm mb-4">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <i className="bi bi-person-vcard text-primary fs-5"></i>
                                    <h6 className="fw-bold mb-0 text-dark">Patient Information</h6>
                                </div>

                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold text-muted">Name</label>
                                        <input
                                            name="name"
                                            className="form-control form-control-sm rounded-3 border-light bg-light"
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="Enter patient name"
                                            required
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold text-muted">Mobile</label>
                                        <input
                                            name="mobile"
                                            className="form-control form-control-sm rounded-3 border-light bg-light"
                                            value={formData.mobile}
                                            onChange={handleChange}
                                            placeholder="Enter mobile number"
                                            required
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold text-muted">Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            className="form-control form-control-sm rounded-3 border-light bg-light"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="Enter email address"
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold text-muted">Age</label>
                                        <input
                                            type="number"
                                            name="age"
                                            className="form-control form-control-sm rounded-3 border-light bg-light"
                                            value={formData.age}
                                            onChange={handleChange}
                                            placeholder="Age"
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold text-muted">DOB</label>
                                        <input
                                            type="date"
                                            name="dob"
                                            className="form-control form-control-sm rounded-3 border-light bg-light"
                                            value={formData.dob}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold text-muted">Gender</label>
                                        <select
                                            name="gender"
                                            className="form-select form-select-sm rounded-3 border-light bg-light"
                                            value={formData.gender}
                                            onChange={handleChange}
                                        >
                                            <option value="1">Male</option>
                                            <option value="2">Female</option>
                                            <option value="3">Other</option>
                                        </select>
                                    </div>

                                    <div className="col-md-12">
                                        <label className="form-label fw-semibold text-muted">Address</label>
                                        <textarea
                                            name="address"
                                            className="form-control rounded-3 border-light bg-light"
                                            rows="2"
                                            value={formData.address}
                                            onChange={handleChange}
                                            placeholder="Enter address"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-4 p-4 shadow-sm mb-4">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <i className="bi bi-calendar-check text-primary fs-5"></i>
                                    <h6 className="fw-bold mb-0 text-dark">Appointment Notes</h6>
                                </div>

                                <div className="row g-3">
                                    <div className="col-md-12">
                                        <label className="form-label fw-semibold text-muted">Notes</label>
                                        <textarea
                                            name="notes"
                                            className="form-control rounded-3 border-light bg-light"
                                            rows="3"
                                            value={formData.notes}
                                            onChange={handleChange}
                                            placeholder="Reason for visit"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-4 p-4 shadow-sm mb-4">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <i className="bi bi-activity text-warning fs-5"></i>
                                    <h6 className="fw-bold mb-0 text-dark">Vitals</h6>
                                </div>

                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <label className="form-label fw-semibold text-muted">BP</label>
                                        <input
                                            name="bp"
                                            className="form-control form-control-sm rounded-3 border-light bg-light"
                                            value={formData.bp}
                                            onChange={handleChange}
                                            placeholder="120/80"
                                        />
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label fw-semibold text-muted">Weight</label>
                                        <input
                                            name="weight"
                                            className="form-control form-control-sm rounded-3 border-light bg-light"
                                            value={formData.weight}
                                            onChange={handleChange}
                                            placeholder="70 kg"
                                        />
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label fw-semibold text-muted">Temperature</label>
                                        <input
                                            name="temperature"
                                            className="form-control form-control-sm rounded-3 border-light bg-light"
                                            value={formData.temperature}
                                            onChange={handleChange}
                                            placeholder="98.6 F"
                                        />
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label fw-semibold text-muted">Heart Rate</label>
                                        <input
                                            name="heartRate"
                                            className="form-control form-control-sm rounded-3 border-light bg-light"
                                            value={formData.heartRate}
                                            onChange={handleChange}
                                            placeholder="80 bpm"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-4 p-4 shadow-sm">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <i className="bi bi-heart-pulse text-danger fs-5"></i>
                                    <h6 className="fw-bold mb-0 text-dark">Medical History</h6>
                                </div>

                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold text-muted">Allergy</label>
                                        <textarea
                                            name="allergy"
                                            className="form-control rounded-3 border-light bg-light"
                                            rows="2"
                                            value={formData.allergy}
                                            onChange={handleChange}
                                            placeholder="None"
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold text-muted">Other Conditions</label>
                                        <textarea
                                            name="otherConditions"
                                            className="form-control rounded-3 border-light bg-light"
                                            rows="2"
                                            value={formData.otherConditions}
                                            onChange={handleChange}
                                            placeholder="Mention conditions"
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold text-muted">Medications</label>
                                        <textarea
                                            name="medications"
                                            className="form-control rounded-3 border-light bg-light"
                                            rows="2"
                                            value={formData.medications}
                                            onChange={handleChange}
                                            placeholder="Current medications"
                                        />
                                    </div>

                                    <div className="col-md-12">
                                        <div className="d-flex flex-wrap gap-3 mt-2">
                                            {[
                                                // ["diabetes", "Diabetes", "bi-droplet-half"],
                                                ["epilepsy", "Epilepsy", "bi-activity"],
                                                ["asthma", "Asthma", "bi-lungs"],
                                            ].map(([name, label, icon]) => (
                                                <label
                                                    key={name}
                                                    className={`border rounded-4 px-4 py-3 d-flex align-items-center gap-3 ${formData[name]
                                                        ? "border-primary bg-primary bg-opacity-10"
                                                        : "bg-light border-light"
                                                        }`}
                                                    style={{ cursor: "pointer", minWidth: "170px" }}
                                                >
                                                    <input
                                                        className="form-check-input m-0"
                                                        type="checkbox"
                                                        name={name}
                                                        checked={formData[name]}
                                                        onChange={handleChange}
                                                    />
                                                    <i className={`bi ${icon} text-primary fs-5`}></i>
                                                    <span className="fw-semibold text-dark">{label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer border-0 bg-white px-4 py-3 flex-shrink-0">
                            <button
                                type="button"
                                className="btn btn-light fw-semibold rounded-3 px-4 py-2"
                                onClick={onClose}
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                className="btn btn-primary fw-bold rounded-3 px-4 py-2 shadow-sm"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check2-circle me-2"></i>
                                        Update Appointment
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}